<template>
  <sc-render :fields="fields" :emptyMessage="$t('NO_DATA_SOURCE_EXIST')">
    <sc-clickable :href="ctaHref" :target="ctaTarget" v-bind="{ ...componentClasses }">
      <TestimonialCard>
        <template #image>
          <media-display
            aspect-ratio="1/1"
            :sc-image-field="imageField"
            :image-desktop-source="imageSrc"
          />
        </template>
        <template #title>
          <span>{{ title || '' }}</span>
        </template>
        <template #firstTag>
          <sc-text v-if="isEditMode || firstTag" class="tag" :field="firstTagField" />
        </template>
        <template #secondTag>
          <sc-text v-if="isEditMode || secondTag" class="tag" :field="secondTagField" />
        </template>
        <template #description>
          <sc-rich-text v-if="isEditMode || description" class="description" tag="div" :field="descriptionField" />
        </template>
        <template #imageCta>
          <sh-button
            v-if="!isEditMode && ctaHref"
            size="small"
            :[btnLinkKey]="ctaHref"
            :color="btnColor"
            :target="ctaTarget"
            :aria-label="ctaHiddenText || ctaText"
            :themeOverride="true"
          >
            {{ ctaText }}
          </sh-button>
        </template>
        <sc-link v-if="isEditMode" class="sc-link" :field="ctaField || {}" />
      </TestimonialCard>
    </sc-clickable>
  </sc-render>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import { Image, Link, Text, RichText } from '@sitecore-jss/sitecore-jss-vue'
import { TestimonialCard } from '@sohohouse/design-system'

import mapFields from '~/helpers/map-fields'
import getButtonColor from '~/helpers/get-button-color'
import { getButtonType } from '~/helpers/get-button-type'
import ThemeMixin from '~/mixins/theme'
import MediaDisplay from '~/components/sitecore/media-display'

import ScRender from '../sc-render'
import ScClickable from '../sc-clickable'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

export default {
  name: 'TestimonialProfile',

  components: {
    ScImage: Image,
    ScLink: Link,
    ScText: Text,
    ScRichText: RichText,
    ScRender,
    ScClickable,
    TestimonialCard,
    MediaDisplay
  },

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  mixins: [ThemeMixin],

  computed: {
    ...mapJssGetters(['isEditMode']),

    ...mapFields({
      ctaField: 'cta',
      ctaHref: 'cta.value.href',
      ctaText: 'cta.value.text',
      ctaTarget: 'cta.value.target',
      ctaHiddenText: 'cta.value.data-hidden-text',
      ctaColor: 'ctaColor.value',
      firstTagField: 'tag1',
      firstTag: 'tag1.value',
      secondTagField: 'tag2',
      secondTag: 'tag2.value',
      descriptionField: 'description',
      description: 'description.value',
      imageField: 'profile.fields.image',
      imageSrc: 'profile.fields.image.value.src',
      titleField: 'profile.fields.title',
      title: 'profile.fields.title.value'
    }),

    isClickable () {
      return !this.isEditMode && this.ctaHref
    },

    btnLinkKey () {
      if (this.isClickable) return ''

      return getButtonType(this.ctaHref)
    },

    btnColor () {
      return getButtonColor(this.ctaColor)
    },

    componentClasses () {
      return {
        class: [
          'testimonial-profile',
          { 'theme-dark': this.isDarkTheme }
        ]
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.testimonial-profile {
  padding: 0 16px;
  margin-bottom: $space-090;
  display: flex;
  justify-content: center;
  text-decoration: none;

  @include tablet {
    padding: 0;
    margin-bottom: $space-110;
  }

  &:first-child {
    margin-top: $space-090;

    @include tablet {
      margin-top: $space-110;
    }
  }

  .tag {
    white-space: nowrap;
  }

  &.theme-dark {
    .description {
      ::v-deep a {
        color: $color-white;
      }
    }
  }
}
</style>
